import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { ClassifyFieldResult } from "@/types/classification";
import { ChevronDown, ChevronRight, FileText, Repeat } from "lucide-react";
import { useState } from "react";
import { PromptDialog } from "./PromptDialog";

interface FieldResultProps {
  onReClassify: (fieldId: string) => void;
  result: ClassifyFieldResult;
  medicalTestDataId: string;
}

export const FieldResult = ({ result, onReClassify }: FieldResultProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);

  return (
    <div className={cn("border rounded-lg p-4 space-y-2", result.error && "border-red-500")}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
          <h3 className="font-medium">{result.fieldName}</h3>
          <div className="text-sm text-muted-foreground">{result.selectedOptions?.length || 0} selected options</div>
        </div>

        <div className="flex items-center gap-2">
          {result.prompt && (
            <Button variant="ghost" onClick={() => setPromptOpen(true)} title="View Prompt">
              <FileText className="h-4 w-4 mr-1" />
              Prompt
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onReClassify(result.templateFieldId)}>
            <Repeat className="h-4 w-4 mr-1" />
          </Button>
        </div>
      </div>

      {result.prompt && (
        <PromptDialog
          open={promptOpen}
          onClose={() => setPromptOpen(false)}
          prompt={result.prompt}
          rawOutput={result.responseText}
        />
      )}

      <div className="pl-10">
        {!isExpanded && (
          <div className="text-sm text-muted-foreground">
            {(result.selectedOptions || []).map((opt) => opt.selectedOptionName).join(", ")}
          </div>
        )}

        {isExpanded && (
          <div className="space-y-4 mt-4">
            {(result.selectedOptions || []).map((option) => (
              <div key={option.selectedOptionId} className="space-y-1">
                <div className="font-medium">{option.selectedOptionName}</div>
                <div className="text-sm text-muted-foreground">{option.reason}</div>
              </div>
            ))}

            {result.debugInfo && (
              <div className="mt-4">
                <div className="font-medium">Debug Info</div>
                <pre className="bg-muted p-3 rounded text-sm whitespace-pre-wrap overflow-x-auto h-full">
                  {JSON.stringify(result.debugInfo, null, 2)}
                </pre>
              </div>
            )}

            {result.error && (
              <div className="mt-4 rounded-md border-2 border-red-500 bg-red-50 p-4">
                <div className="font-medium text-red-800 mb-1">Error</div>
                <div className="text-sm text-red-600 whitespace-pre-wrap break-words">{result.error}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
